import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, mergeMap} from 'rxjs/operators';
import { GetEmpty } from 'src/app/classes/Empties';
import { NumeroALetras } from 'src/app/classes/NumeroALetras';
import {ItemOptionInfo, OrderInfo, OrderItemInfo, QuoteInfo, QuoteItemInfo, UserBalance} from 'src/app/models/models';
import {Address, Batch_Record, Currency, Item_Exception, Order, Order_Item, Order_Item_Batch, Order_Item_Exception, Price, Stock_Record, Store} from 'src/app/models/RestModels';
import {BaseComponent} from '../base/base.component';
import { environment } from 'src/environments/environment';
import { CurrencyPipe } from '@angular/common';

@Component({
	selector: 'app-view-quote',
	templateUrl: './view-quote.component.html',
	styleUrls: ['./view-quote.component.css']
})
export class ViewQuoteComponent extends BaseComponent implements OnInit
{
	quote_info:QuoteInfo | null = null;
	total:number = 0;
	store_list: Store[];
	billing_address: Address | null = null;
	print80mm:boolean = false;

	show_resend_quote:boolean = false;
	resend_quote_name:string = '';
	resend_quote_email:string = '';
	billing_data = GetEmpty.billing_data();
	total_letra:string = '';
	currency:Currency = GetEmpty.currency();
	currency_list: Currency[];
	total_tax: number = 0;
	store:Store = GetEmpty.store();
	user_balance:UserBalance = null;

	subtotal:number = 0;

	// Batch selection
	show_batch_selection: boolean = false;
	batch_items_pending: {qii: QuoteItemInfo, batches: Batch_Record[], selections: {batch: Batch_Record, qty: number}[]}[] = [];
	current_batch_item_index: number = 0;
	send_to_credit_pending: boolean = false;

	// View batches
	show_view_batches: boolean = false;
	view_batches_loading: boolean = false;
	view_batches_list: Batch_Record[] = [];

	// Installments
	show_installments: boolean = false;
	installment_months: number = 0;
	installment_round_amount: number = 0;
	first_payment_date: string = '';
	frequency: 'WEEKLY' | 'MONTHLY' | '2X_MONTHLY' | 'NONE' = 'MONTHLY';

	ngOnInit(): void
	{
		this.sink = this.route.paramMap.pipe
		(
			mergeMap((param_map)=>
			{
				return forkJoin
				({
					stores: this.rest.getStores(),
					currencies: this.rest.currency.search({limit:99999}),
					quote_info:this.rest.quote_info.get(param_map.get('id'))
				})
			}),
			mergeMap((responses)=>
			{
				this.currency_list = responses.currencies.data;
				this.store = responses.stores.data.find(s=>s.id == responses.quote_info.quote.store_id) as Store;

				let obs:Observable<Address | null> | null = null;

				if( !responses.quote_info?.client_user )
				{
					obs = of(null);
				}
				else
				{
					if( responses.quote_info.client_user.default_billing_address_id )
					{
						obs = this.rest.address.get( responses.quote_info.client_user.default_billing_address_id);
					}
					else ( responses.quote_info?.client_user )
					{
						obs = this.rest.address.search
						({
							eq		:{ user_id:responses.quote_info.client_user.id },
							nn		:['rfc'],
							limit	:1
						})
						.pipe
						(
							mergeMap((response)=>
							{
								if( response.total > 0 )
								{
									return of( response.data[0] );
								}
								else
								{
									return of( null );
								}
							})
						);
					}
				}

				return forkJoin({
					quote_info:of(responses.quote_info),
					stores: of( responses.stores ),
					address: obs,
					billing_data: this.rest.billing_data.get( this.store.default_billing_data_id),
					balance: this.searchUserBalance( responses.quote_info.quote.client_user_id ),
					creator: responses.quote_info.quote.created_by_user_id ? this.rest.user.get( responses.quote_info.quote.created_by_user_id ) : of(null)
				})
			})
		)
		.subscribe((response)=>
		{
			this.currency = this.currency_list.find(c=>c.id == response.quote_info.quote.currency_id) as Currency;
			this.billing_data = response.billing_data;
			this.billing_address = response.address;
			this.store_list = response.stores.data;
			this.quote_info = response.quote_info;
			this.quote_info.creator = response.creator;
			this.total = response.quote_info.items.reduce((p,i)=>p+Math.floor(i.quote_item.total*100)/100,0);
			this.total_tax = response.quote_info.items.reduce((p,i)=>p+i.quote_item.tax, 0);
			this.subtotal = response.quote_info.items.reduce((p,i)=>p+i.quote_item.subtotal,0);
			let n = new NumeroALetras('','','/100','/100');
			this.total_letra = n.numeroALetras( this.total );
			this.user_balance = response.balance;

		},(error)=>this.showError(error));
	}

	printDirect(evt:Event)
	{
		this.print( evt );
	}

	print(evt:Event)
	{
		this.rest.hideMenu();
		setTimeout(()=>{
			console.log( evt );
			window.print();
		},200);
	}

	designCustomQuote()
	{
		this.router.navigate(['/custom-quote-designer', this.quote_info.quote.id]);
	}

	getOrderItemFromQuoteItem(qii: QuoteItemInfo, item_group: number, sat_ieps: number): OrderItemInfo
	{
		let order_item = GetEmpty.order_item(qii.item);

		order_item.tax_included = qii.quote_item.tax_included;
		// Hay un error en la cotización los precios los pone mal
		// Como siempre los pone con iva lo ponemos hardcoded HERE
		order_item.discount = qii.quote_item.discount;
		order_item.discount_percent = qii.quote_item.discount_percent;
		order_item.subtotal = qii.quote_item.subtotal;
		order_item.total = qii.quote_item.total;
		order_item.qty = qii.quote_item.qty;
		order_item.tax = qii.quote_item.tax;
		order_item.status = qii.quote_item.status;
		order_item.unitary_price = qii.quote_item.unitary_price;
		order_item.unitary_price_meta = qii.quote_item.unitary_price_meta;
		order_item.original_unitary_price = qii.quote_item.original_unitary_price;
		order_item.note = qii.quote_item.note;

		// Preserve item options grouping from quote
		order_item.item_option_id = qii.quote_item.item_option_id || null;
		order_item.item_option_qty = qii.quote_item.item_option_qty || 0;
		order_item.item_group = qii.quote_item.item_group || item_group;

		return {
			order_item,
			item: qii.item,
			category: qii.category,
			prices: [] as Price[],
			records: [] as Stock_Record[],
			options: [] as ItemOptionInfo[],
			exceptions: [] as Item_Exception[],
			order_item_exceptions: [] as Order_Item_Exception[],
			serials: [],
			serials_string: '',
			batches: [] as Order_Item_Batch[],
			created: new Date(),
			commanda_type_id: qii.item.commanda_type_id,
		};
	}


	searchUserBalance(user_id:number):Observable<UserBalance|null>
	{
		if( this.rest.is_offline )
			return of( null );

		return this.rest.user.search({csv:{ id:[ user_id ]},limit:1}).pipe
		(
			mergeMap((response)=>{

				if( response.data.length )
					return of( response.data[0] );

				return of( null );
			})
		);
	}

	startCreateOrderProcess()
	{
		let send_to_credit:boolean;

		//comparar si la cotizacion tiene client_user_id
		if( this.quote_info?.quote?.client_user_id )
		{
			//comparar si el cliente tiene credito suficiente para la compra
			let balance_futuro = Math.abs( this.user_balance.balance - this.total );

			if( this.quote_info.client_user.credit_limit > balance_futuro )
			{
				send_to_credit = true;
				this.showInstallmentsModal();
			}
			else
			{
				//mostrar modal para confirmar la compra
				this.subs.sink = this.confirmation.showConfirmAlert
				(
					this.quote_info,
					'Credito Insuficiente',
					'Verifique los adeudos del cliente o incremente su limite de credito.',
					'Crear orden a contado'
				)
				.subscribe((response)=>
				{
					if( response.accepted )
					{
						send_to_credit = false;
						this.proceedWithBatchCheck(send_to_credit);
					}
				});
			}
		}
		else
		{
			console.log('No tiene cliente, creando orden');
			send_to_credit = false;
			this.proceedWithBatchCheck(send_to_credit);
		}
	}

	showInstallmentsModal()
	{
		this.first_payment_date = new Date().toISOString().split('T')[0];
		this.installment_months = 0;
		this.show_installments = true;
	}

	cancelInstallments()
	{
		this.show_installments = false;
	}

	confirmInstallments()
	{
		if (this.installment_months > 0)
		{
			if (!this.first_payment_date || !this.frequency)
			{
				this.showError('La fecha de primer pago y frecuencia son requeridas si hay mensualidades');
				return;
			}
		}
		this.show_installments = false;
		this.proceedWithBatchCheck(true);
	}

	proceedWithBatchCheck(send_to_credit: boolean)
	{
		let batch_items = this.quote_info.items.filter(qii =>
			qii.item.batch_option !== 'NONE' && qii.quote_item.status == 'ACTIVE'
		);

		if (batch_items.length === 0)
		{
			this.createOrder(send_to_credit);
			return;
		}

		this.send_to_credit_pending = send_to_credit;
		this.batch_items_pending = batch_items.map(qii => ({
			qii,
			batches: [] as Batch_Record[],
			selections: [] as {batch: Batch_Record, qty: number}[]
		}));
		this.current_batch_item_index = 0;
		this.loadBatchesForCurrentItem();
	}

	loadBatchesForCurrentItem()
	{
		let pending = this.batch_items_pending[this.current_batch_item_index];
		this.is_loading = true;

		this.subs.sink = this.rest.batch_record.search({
			eq: {
				item_id: pending.qii.item.id,
				store_id: this.quote_info.quote.store_id,
				is_current: 1
			},
			gt: { qty: 0 },
			limit: 1000
		}).subscribe(
			(response) => {
				pending.batches = response.data;
				pending.selections = response.data.map(b => ({ batch: b, qty: 0 }));
				this.is_loading = false;
				this.show_batch_selection = true;
			},
			(error) => {
				this.is_loading = false;
				this.showError(error);
			}
		);
	}

	getCurrentBatchPending()
	{
		return this.batch_items_pending[this.current_batch_item_index];
	}

	onBatchConfirm()
	{
		let pending = this.batch_items_pending[this.current_batch_item_index];
		let total_selected = pending.selections.reduce((sum, s) => sum + s.qty, 0);
		let required_qty = pending.qii.quote_item.qty;

		if (total_selected !== required_qty)
		{
			this.showError(`La cantidad seleccionada (${total_selected}) debe ser igual a la cantidad requerida (${required_qty})`);
			return;
		}

		// Move to next batch item or finish
		this.current_batch_item_index++;

		if (this.current_batch_item_index < this.batch_items_pending.length)
		{
			this.show_batch_selection = false;
			setTimeout(() => this.loadBatchesForCurrentItem(), 200);
		}
		else
		{
			this.show_batch_selection = false;
			this.createOrder(this.send_to_credit_pending);
		}
	}

	onBatchCancel()
	{
		this.show_batch_selection = false;
		this.batch_items_pending = [];
		this.current_batch_item_index = 0;
	}

	viewBatches(qii: QuoteItemInfo)
	{
		this.show_view_batches = true;
		this.view_batches_loading = true;
		this.view_batches_list = [];

		this.subs.sink = this.rest.batch_record.search({
			eq: {
				item_id: qii.item.id,
				store_id: this.quote_info.quote.store_id,
				is_current: 1
			},
			gt: { qty: 0 },
			limit: 1000
		}).subscribe(
			(response) => {
				this.view_batches_list = response.data;
				this.view_batches_loading = false;
			},
			(error) => {
				this.view_batches_loading = false;
				this.showError('Error al cargar lotes');
			}
		);
	}

	createOrder(send_to_credit: boolean) {
		this.is_loading = true;
		let version_created = this.rest.getVersion();

		let order: Order = {
			quote_id: this.quote_info.quote.id as number,
			address: '',
			amount_paid: 0,
			ares: 0,
			authorized_by: null,
			billing_address_id: null,
			billing_data_id: null,
			cancellation_reason: '',
			cancellation_timestamp: null,
			cancelled_by_user_id: null,
			cashier_user_id: this.rest.current_user.id,
			city: '',
			client_name: this.quote_info.quote.name,
			client_user_id: this.quote_info.quote.client_user_id,
			closed_timestamp: null,
			created: new Date(),
			currency_id: this.quote_info.quote.currency_id,
			delivery_schedule: null,
			delivery_status: 'PENDING',
			delivery_user_id: null,
			discount: 0,
			discount_calculated: 0,
			external_id: null,
			facturacion_code: '',
			//facturacion_timestamp: null,
			guests: 1,
			installment_months: send_to_credit ? this.installment_months : 0,
			installment_round_amount: send_to_credit ? this.installment_round_amount : 0,
			first_payment_date: (send_to_credit && this.installment_months > 0) ? this.first_payment_date : null,
			frequency: (send_to_credit && this.installment_months > 0) ? this.frequency : 'NONE',
			lat: null,
			lng: null,
			marked_for_billing: 'NO',
			paid_status: 'PENDING',
			period_id: null,
			price_type_id: this.quote_info?.client_user?.price_type_id || 1,
			sat_domicilio_fiscal_receptor: '',
			sat_exchange_rate: 22,
			sat_factura_id: null,
			sat_ieps: 0,
			sat_isr: 0,
			sat_regimen_capital_receptor: '',
			sat_regimen_fiscal_receptor: '',
			service_type: 'QUICK_SALE',
			status: 'CLOSED',
			store_id: this.quote_info.quote.store_id,
			sync_id: this.rest.getSyncId(),
			system_activated: null,
			table_id: null,
			tag: '',
			tax: 0,
			tax_percent: this.quote_info.quote.tax_percent,
			total: this.total,
			updated: new Date(),
			version_created,
			version_updated: version_created
		};

		let item_group: number = Date.now();

		console.log('Creando ', order.currency_id);

		let order_info: OrderInfo = {
			order: order,
			client: null,
			purchase: null,
			cashier: this.rest.current_user,
			sat_factura: null,
			delivery_user: null,
			items: this.buildOrderItems(item_group, order.sat_ieps),
			structured_items: [],
			store: GetEmpty.store(),
			offers: []
		};

		this.subs.sink = this.rest.order_info
		.create(order_info)
		.pipe
		(
			mergeMap((response) =>
			{
				return forkJoin
				({
					order_info: of(response),
					update: this.rest.setOrderActive(response.order.id)
				});
			}),
			mergeMap((response) => {
				this.is_loading = false;
				this.showSuccess('La orden se ha creado correctamente');

				let payload = { order_id: response.order_info.order.id };
				let closed_log = (r): Observable<any> => {
					console.log('Orden Cerrada');
					return of(null);
				};

				let obs = !send_to_credit
					? of(null)
					: this.rest.update('closeOrder', payload).pipe(
						mergeMap(closed_log),
						catchError((e) => of(null))
					);

				return forkJoin({
					order_info: of(response.order_info),
					closed: obs
				});
			})
		)
		.subscribe
		(
			(response) =>
			{
				this.router.navigate(['/view-order', response.order_info.order.id]);
			},
			(error) => this.showError(error)
		);
	}

	buildOrderItems(item_group: number, sat_ieps: number): OrderItemInfo[]
	{
		let result: OrderItemInfo[] = [];
		let index = 0;

		for (let qii of this.quote_info.items)
		{
			if (qii.quote_item.status !== 'ACTIVE') continue;

			let batch_pending = this.batch_items_pending.find(bp => bp.qii === qii);

			let oii = this.getOrderItemFromQuoteItem(qii, item_group + index, sat_ieps);

			if (batch_pending)
			{
				let selected = batch_pending.selections.filter(s => s.qty > 0);
				let total_qty = selected.reduce((a, s) => a + s.qty, 0);
				oii.order_item.qty = total_qty;

				oii.batches = selected.map(sel => ({
					id: 0,
					order_item_id: 0,
					batch: sel.batch.batch,
					expiration_date: sel.batch.expiration_date,
					qty: sel.qty,
					created: new Date(),
					updated: new Date()
				}));

				oii.order_item.subtotal = oii.order_item.unitary_price * total_qty;
				oii.order_item.tax = oii.order_item.subtotal * (this.quote_info.quote.tax_percent / 100);
				oii.order_item.total = oii.order_item.subtotal + oii.order_item.tax;
			}

			result.push(oii);
			index++;
		}

		return result;
	}

	showSendQuote()
	{
		this.resend_quote_name = this.quote_info.quote.name;
		this.resend_quote_email = this.quote_info.quote.email;
		this.show_resend_quote = true;
	}

	sendQuote(evt:Event)
	{
		this.is_loading = true;
		let x:any = evt

		let client_name = this.quote_info.quote.name;
		let email = this.resend_quote_email;
		let quote_id = this.quote_info.quote.id;
		let date = new Date().toISOString().split('T')[0];
		let valid_until = this.quote_info.quote.valid_until;
		let phone = this.quote_info.quote.phone;
		let total = this.total;
		let image_html = '';
		let phone_html = '';
		let currency = this.currency.name;

		if( phone )
		{
			phone_html = `
							<tr>
								<th style="text-align:left" >Teléfono:</th>
								<td>${phone}</td>
							</tr>`;
		}

		if( this.store.ticket_image_id || this.rest.local_preferences.default_ticket_image_id )
		{
			let path = this.rest.getImagePath(this.store.ticket_image_id, this.rest.local_preferences.default_ticket_image_id);
			image_html = `<img src="${path}" style="width:150px;aspect-ratio: auto;">`;
		}

		let currency_pipe = new CurrencyPipe('en-US');

		let html = `<DOCTYPE html>
		<html>
		<head></head>
		<body>
				${image_html}
				<table style="width:100%;text-align:left">
					<tr>
						<td style="width:50%">
							<table style="text-align:left">
								<tr>
									<th style="text-align:left">Nombre:</th>
									<td>${client_name}</td>
								</tr>
								<tr>
									<th style="text-align:left" >Correo:</th>
									<td>${email}</td>
								</tr>
								${phone_html}
							</table>
						</td>
						<td style="width:50%">
							<table style="text-align:left;float:right">
								<tr>
									<th style="text-align:left">Cotización:</th>
									<td>NO. ${quote_id}</td>
								</tr>
								<tr>
									<th style="text-align:left">Fecha de elaboración:</th>
									<td>${date}</td>
								</tr>
								<tr>
									<th style="text-align:left">Vigencia:</th>
									<td>${valid_until}</td>
								</tr>
							</table>
						</td>
					</tr>
				</table>
				<hr>
				<table style="width:100%;text-align:left">
					<thead class="thead-light">
						<tr class="table-secondary">
							<th style="text-align:left">Artículo</th>
							<th style="text-align:right">Cantidad</th>
							<th style="text-align:right">Precio</th>
							<th style="text-align:right">Total</th>
						</tr>
					</thead>
					<tbody>`;

		this.quote_info.items.forEach((i) =>
		{
			html += `
						<tr>
							<td>${i.item.name}</td>
							<td style="text-align:right">${i.quote_item.qty}</td>
							<td style="text-align:right">${i.quote_item.unitary_price}</td>
							<td style="text-align:right">${currency_pipe.transform(Math.floor(i.quote_item.total*100)/100)}</td>
						</tr>`;
		});

		html += `
					</tbody>
					<tfoot class="thead-light">
						<tr class="table-secondary">
							<th colspan="3" style="text-align:right">Total</th>
							<th style="text-align:right">${currency_pipe.transform(Math.floor(total*100)/100)}</th>
						</tr>
					</tfoot>
				</table>
				<h3>TERMINOS Y CONDICIONES</h3>
				<ul>
					<li>PRECIO EXPRESADO EN: <b>${currency.toUpperCase()}</b>
					<li>CONDICIONES DE PAGO: <b>CONTADO</b>
				</ul>
			</body>
		</html>`;

		let html_object =
		{
			html: html,
			orientation: 'P',
			default_font_size: 9,
			default_font: 'Helvetica',
			download_name: ''
		}

		let url = environment.app_settings.pdf_service_url + "/index.php";

		this.sink = this.rest.externalPost(url, html_object)
		.pipe
		(
			mergeMap((response) =>
			{
				const pdf_file = new File([response], 'cotizacion.pdf', { type: 'application/pdf' });

				const formData = new FormData();
				formData.append('pdf', pdf_file);
				formData.append('quote_id', this.quote_info.quote.id.toString());
				formData.append('email', this.resend_quote_email);
				formData.append('name', this.resend_quote_name || '');

				return this.rest.send_quote.create(formData);
			})
		)
		.subscribe
		({
			next: (response) =>
			{
				this.showSuccess('El correo se envió correctamente');
				this.is_loading = false;
				x.target.reset();
			},
			error: (error) =>
			{
				this.showError('Error al enviar el correo');
				this.is_loading = false;
			}
		});
	}
}
